import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password)
      return setError('Please enter email and password');
    setLoading(true);
    try {
      const { data, error: authErr } =
        await supabase.auth.signInWithPassword({
          email: email.trim(), password,
        });
      if (authErr) throw authErr;

      const uid = data.user?.id;
      if (!uid) throw new Error('Login failed');

      // Check if admin
      const { data: adminData } = await supabase
        .from('admin_users')
        .select('id')
        .eq('auth_user_id', uid)
        .maybeSingle();

      if (adminData) {
        navigate('/admin/dashboard');
        return;
      }

      // Check company
      const { data: companyData } = await supabase
        .from('companies')
        .select('id, verification_status, account_deleted')
        .eq('auth_user_id', uid)
        .maybeSingle();

      if (!companyData) {
        await supabase.auth.signOut();
        throw new Error('No company account found. Please register first.');
      }

      if (companyData.account_deleted) {
        await supabase.auth.signOut();
        throw new Error('This account has been deleted.');
      }

      // All statuses can login — dashboard handles restrictions
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed. Check credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#F8FAFF,#EEF2FF)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: 24,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center',
            gap: 10, marginBottom: 6,
          }}>
            <div style={{
              width: 42, height: 42,
              background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              borderRadius: 12, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 900, fontSize: 20,
            }}>U</div>
            <span style={{
              fontWeight: 800, fontSize: 20,
              background: 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: 1,
            }}>UNIVERSE Careers</span>
          </div>
          <p style={{ color: '#64748B', fontSize: 13 }}>
            Company & Admin Portal
          </p>
        </div>

        <div style={{
          background: '#fff', borderRadius: 20,
          padding: 36,
          boxShadow: '0 4px 40px rgba(79,142,247,0.08)',
          border: '1px solid #E2E8F0',
        }}>
          <h2 style={{
            fontSize: 22, fontWeight: 800,
            color: '#1E293B', marginBottom: 4,
          }}>Welcome back</h2>
          <p style={{
            color: '#94A3B8', fontSize: 13, marginBottom: 24,
          }}>Sign in to manage your account</p>

          {error && (
            <div style={{
              background: '#FFF1F2', border: '1px solid #FECDD3',
              borderRadius: 10, padding: '10px 14px',
              marginBottom: 20, display: 'flex',
              alignItems: 'center', gap: 8,
              color: '#E11D48', fontSize: 13,
            }}>
              <AlertCircle size={15} />{error}
            </div>
          )}

          <form onSubmit={handleLogin}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Email */}
            <div>
              <label style={{
                display: 'block', fontSize: 12,
                fontWeight: 600, color: '#475569', marginBottom: 6,
              }}>Email Address</label>
              <div style={{ position: 'relative' }}>
                <Mail size={16} style={{
                  position: 'absolute', left: 12,
                  top: '50%', transform: 'translateY(-50%)',
                  color: '#94A3B8',
                }} />
                <input type="email" value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  style={{
                    width: '100%',
                    padding: '11px 14px 11px 38px',
                    border: '1.5px solid #E2E8F0',
                    borderRadius: 10, fontSize: 14,
                    color: '#1E293B', background: '#F8FAFF',
                    outline: 'none',
                  }}
                  onFocus={e => e.target.style.borderColor = '#4F8EF7'}
                  onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label style={{
                display: 'block', fontSize: 12,
                fontWeight: 600, color: '#475569', marginBottom: 6,
              }}>Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={16} style={{
                  position: 'absolute', left: 12,
                  top: '50%', transform: 'translateY(-50%)',
                  color: '#94A3B8',
                }} />
                <input
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{
                    width: '100%',
                    padding: '11px 40px 11px 38px',
                    border: '1.5px solid #E2E8F0',
                    borderRadius: 10, fontSize: 14,
                    color: '#1E293B', background: '#F8FAFF',
                    outline: 'none',
                  }}
                  onFocus={e => e.target.style.borderColor = '#4F8EF7'}
                  onBlur={e => e.target.style.borderColor = '#E2E8F0'}
                />
                <button type="button"
                  onClick={() => setShowPass(!showPass)}
                  style={{
                    position: 'absolute', right: 12,
                    top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none',
                    cursor: 'pointer', color: '#94A3B8',
                    display: 'flex', alignItems: 'center',
                  }}>
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading}
              style={{
                width: '100%', padding: '13px',
                border: 'none', borderRadius: 12,
                background: loading ? '#CBD5E1'
                  : 'linear-gradient(135deg,#4F8EF7,#7C3AED)',
                color: '#fff', fontWeight: 700,
                fontSize: 15,
                cursor: loading ? 'not-allowed' : 'pointer',
                marginTop: 4,
                boxShadow: loading ? 'none'
                  : '0 4px 16px rgba(79,142,247,0.3)',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: 8,
              }}>
              {loading ? (
                <div style={{
                  width: 18, height: 18,
                  border: '2px solid rgba(255,255,255,0.4)',
                  borderTopColor: '#fff', borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
              ) : 'Sign In'}
            </button>
          </form>

          <p style={{
            textAlign: 'center', fontSize: 13,
            color: '#94A3B8', marginTop: 20,
          }}>
            Don't have an account?{' '}
            <Link to="/register" style={{
              color: '#4F8EF7', fontWeight: 600,
            }}>Register your company</Link>
          </p>
        </div>
      </div>
    </div>
  );
}